import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import Editor from '../../Common/Editor';
import { EditProperty } from './EditUtils';
import DocHistory from './EditAreaPanels/DocHistory';
import DocInfo from './EditAreaPanels/DocInfo';
import RequestQA from './EditAreaPanels/RequestQA';
import PopUp from '../../Common/PopUp';
import { showToast } from '../../Common/Messages';
import DocFeedback from './EditAreaPanels/DocFeedback';
import { convertDataType } from '../../Common/EditorTools';
import SearchDocument from '../../Common/SearchDocument';

const getMetadataKeywords = (metadata) => {
    const requiredFields = metadata?.required_fields || {};
    const keywordSources = [
        requiredFields.keywords,
        requiredFields.keyword,
        metadata?.keywords,
        metadata?.keyword
    ];

    const keywordKeys = new Set();
    return keywordSources
        .flatMap(source => Array.isArray(source) ? source : typeof source === "string" ? source.split(",") : [])
        .map(keyword => `${keyword}`.trim())
        .filter(keyword => {
            const key = keyword.toLowerCase();
            if (keyword === "" || keywordKeys.has(key)) {
                return false;
            }

            keywordKeys.add(key);
            return true;
        });
}

const EditArea = (props) => {    
    const LoginStates = useSelector(state => state.login);

    const [editor, setEditor] = useState(null);
    const [docId, setDocId] = useState("");
    const [valid, setValid] = useState({isValid: true, message: ""});
    const [sidePanelType, setSidePanelType] = useState("");   
    const [isViewMode, setIsViewMode] = useState(true);
    const [popUpContent, setPopUpContent] = useState(null);
    const [removeData, setRemoveData] = useState([]);
    const validRef = useRef(valid);
    const [accessType, setAccessType] = useState([]);

    useEffect(() => {
        getAccessType();
    }, []);

    useEffect(() => {
        validRef.current = valid;
    }, [valid]);

    useEffect(() => {
        if(props.current && props.current.item) {
            setDocId(props.current.item.docId);
        } else {
            setDocId("");
        }
    }, [props.current]);
    useEffect(() => {
        if (!props.isEditable) {
            setIsViewMode(true);
        } else if(props.editorData && props.editorData.workStatus && props.editorData.workStatus === "CheckOut" 
            && LoginStates.userId === props.editorData.updateUserId) {
            setIsViewMode(false);
        } else {
            setIsViewMode(true);
        }
    }, [props.editorData]);
    useEffect(() => {
        setSidePanelType("info");
    }, [docId]);

    const getAccessType = () => {
        fetch('datas/oper/accesstype.json',
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            }).then((resp) => resp.json())
            .then(data => {
                setAccessType(data);
            }).catch(e => {
            });
    }

    const handleChange = (e) => {
        props.setEditorData(prev => {return {...prev, title: e.target.value}});
    }

    const CopyFromDocument = (from) => {
        openSearchDoc(from, 'copy');
    }

    const pasteDocumentData = async (copyData, fromDocId) => {
        if(props.editorData.data?.blocks?.length > 0 && 
            (props.editorData.data?.blocks[0].type !== "paragraph" || props.editorData.data?.blocks[0].data.text.trim() !== ""))
            if(!window.confirm("현재 문서의 내용이 사라집니다. 계속 진행하시겠습니까?")) return;
        if(fromDocId)
            copyData = await props.copyDocument(fromDocId, `${props.currentDocBox.docBoxPath.toLowerCase()}/${docId}`);
        if(!editor || ! copyData) return;
        const docData = JSON.parse(copyData);
        if(docData.data?.blocks) {
            docData.data?.blocks.forEach(block => {
                delete block.id;
            });
        }
        const newData = {
            ...props.editorData,
            tag: docData.tag,
            relationInfo: docData.relationInfo,
            faq: docData.faq || [],
            data: docData.data
        }
        props.setEditorData(newData);
        editor.render(convertDataType(docData.data, props.currentDocBox.docBoxPath, docId));
        closeRelatedDoc();
    }
    
    const mergeKeywordsToTags = (editorData, keywords) => {
        if (keywords.length === 0) {
            return editorData;
        }

        const currentTags = Array.isArray(editorData?.tag) ? editorData.tag : [];
        const tagKeys = new Set(currentTags.map(tag => `${tag}`.trim().toLowerCase()));
        const mergedTags = [...currentTags];

        keywords.forEach(keyword => {
            const key = keyword.toLowerCase();
            if (!tagKeys.has(key)) {
                tagKeys.add(key);
                mergedTags.push(keyword);
            }
        });

        return {
            ...editorData,
            tag: mergedTags
        };
    }

    const createDocumentRequestBody = (comment = "", editorData = props.editorData) => {
        const userId = LoginStates.userId;
        const docData = JSON.stringify(editorData);
        return {
            folderName: props.currentDocBox.docBoxPath.toLowerCase(),
            devUserId: userId,
            docData: docData,
            docId: props.current.item.docId,
            companySeq: LoginStates.companySeq,
            comment: comment
        };
    }

    const showMetadataExtractionError = (message, comment) => {
        setPopUpContent(
            <div className="msg-popup center-box">
                <div className="modal">
                    <div className="modal-top">
                        <h3 className="modal-title">태그 추출 실패</h3>
                        <button className="btn-close" onClick={() => setPopUpContent(null)}>
                            <i className="icoKdc32 close"></i>
                        </button>
                    </div>
                    <div className="modal-cnts">
                        <p>{message || "태그를 추출하는 중 오류가 발생했습니다."}</p>
                        <p>태그 추출을 건너뛰고 편집 완료를 진행하시겠습니까?</p>
                    </div>
                    <div className="alert-btn-wrap">
                        <button className="btn-secondary" onClick={() => setPopUpContent(null)}>취소</button>
                        <button className="btn-primary" onClick={() => {
                            setPopUpContent(null);
                            onEditDocument("EditEnd", comment, null);
                        }}>건너뛰고 편집 완료</button>
                    </div>
                </div>
            </div>
        );
    }

    const previewEditEndMetadata = async (body, comment) => {
        setPopUpContent(
            <div className="msg-popup center-box">
                <div className="modal">
                    <div className="modal-top">
                        <h3 className="modal-title">메타데이터 추출</h3>
                    </div>
                    <div className="modal-cnts">
                        <p>문서 메타데이터를 추출하고 있습니다.</p>
                    </div>
                </div>
            </div>
        );

        let response;
        let result;
        try {
            response = await fetch('api/Edit/PreviewEditEndMetadata', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json; charset=utf-8'
                },
                body: JSON.stringify(body)
            });
            result = await response.json();
        } catch (error) {
            showMetadataExtractionError(error?.message, comment);
            return;
        }

        if(response.status !== 200) {
            showMetadataExtractionError(result?.message, comment);
            return;
        }

        if(result.status === "Skipped") {
            setPopUpContent(
                <div className="msg-popup center-box">
                    <div className="modal">
                        <div className="modal-top">
                            <h3 className="modal-title">메타데이터 추출 건너뜀</h3>
                            <button className="btn-close" onClick={() => setPopUpContent(null)}>
                                <i className="icoKdc32 close"></i>
                            </button>
                        </div>
                        <div className="modal-cnts">
                            <p>{result.message}</p>
                        </div>
                        <div className="alert-btn-wrap">
                            <button className="btn-secondary" onClick={() => setPopUpContent(null)}>취소</button>
                            <button className="btn-primary" onClick={() => {
                                setPopUpContent(null);
                                onEditDocument("EditEnd", comment, null);
                            }}>편집 완료</button>
                        </div>
                    </div>
                </div>
            );
            return;
        }

        setPopUpContent(
            <MetadataPreviewPopup
                metadata={result.metadata}
                currentTags={props.editorData?.tag}
                onClose={() => setPopUpContent(null)}
                onApprove={(selectedKeywords) => {
                    setPopUpContent(null);
                    onEditDocument("EditEnd", comment, selectedKeywords);
                }}
            />
        );
    }

    const onEditDocument = async (status, comment = "", approvedKeywords = undefined) => {
        if(status === 'Update'){
            if(!validRef.current.isValid) {
                showToast('warning', validRef.current.message);
                return;
            }
        }
        if(status === 'Update' || status === 'CheckIn') {
            if (removeData.length) await removeFileData(removeData);
        }
        if(!status || status === "") return;

        const editorDataForRequest = status === "EditEnd" && Array.isArray(approvedKeywords)
            ? mergeKeywordsToTags(props.editorData, approvedKeywords)
            : props.editorData;

        if (editorDataForRequest !== props.editorData) {
            props.setEditorData(editorDataForRequest);
        }

        const body = createDocumentRequestBody(comment, editorDataForRequest);
        if(status === "EditEnd" && approvedKeywords === undefined) {
            await previewEditEndMetadata(body, comment);
            return;
        }

        const response = await fetch('api/Edit/' + status, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(body)
        });
        const result = await response.json();
        if(response.status === 200){
            if (status === "Update") {
                showToast('success', "문서가 저장되었습니다."); 
                props.setIsUpdate(true);
            } else if (status === "Delete") {
                props.removeNode(props.current);
                showToast('success', "문서가 삭제되었습니다.");                                
            } else if(status === "EditEnd") {
                if(editorDataForRequest.tag && editorDataForRequest.tag.length > 0) {
                    const isQA = window.confirm("편집이 완료되었습니다. 검수 요청을 진행하시겠습니까?");
                    if(isQA) {
                        SubmitQA(editorDataForRequest);
                    }
                }
            } else if (status === "EditCancle") {
                showToast('success', "편집이 취소되었습니다.");
                props.setIsUpdate(true);
            }
            if(status !== "Delete")
                props.getManual(props.current);
            else 
                props.setCurrent(null); // delete일 경우 current 초기화
        } else {
            showToast('danger', result.message);
        }
    }
    const SubmitQA = (editorDataOverride = null) => {
        const editorData = editorDataOverride || props.editorData;
        if(!editorData.tag || editorData.tag.length === 0) {
            showToast("warning", "태그를 입력해주세요.");
            return;
        }
        setPopUpContent(<RequestQA onClose={setPopUpContent} onSubmit={onQADocument} />);        
        return;
    }    
    const addRelatedDoc = (item, type) => {
        props.setEditorData(prev => { return {...prev, [type]: [...(prev[type] || []), item]} });
    }

    const openSearchDoc = (from, type="relation") => {
        let documents = "";
        if(from === "faq") {
            documents = props.editorData.faq ?? [];
        } else {
            documents = props.editorData.relationInfo;
        }
        if(docId !== "" && !isViewMode)
            setPopUpContent(<SearchDocument from={from} type={type} documents={documents} 
                addItem={addRelatedDoc} removeItem={removeRelatedDoc} fnBtnOnPopup={closeRelatedDoc} pasteDocumentData={pasteDocumentData} />);
    }

    const removeRelatedDoc = (item, type) => {
        if (type === "relationInfo") {
            const relatedDocumentData = props.editorData.relationInfo?.filter(doc => doc.manualId !== item.manualId);
            props.setEditorData(prev => {return {...prev, relationInfo: relatedDocumentData}});
        } else {
            const faqDocumentData = props.editorData.faq?.filter(doc => doc.faqID !== item.faqID);
            props.setEditorData(prev => {return {...prev, faq: faqDocumentData}});
        }
    }

    const closeRelatedDoc = () => {
        setPopUpContent(null);
    }

    const formattedHistoryDateTime = (dateTime) => {
        if (!dateTime) return "-";
        const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
        if (Number.isNaN(date.getTime())) return "-";

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    const restoreHistoryPopupStyle = {
        popup: {
            minWidth: '420px'
        },
        alertBox: {
            width: '420px',
            padding: '36px'
        },
        message: {
            margin: '16px 0 20px 0',
            color: '#666',
            fontSize: '14px'
        },
        info: {
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '28px',
            padding: '16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: '#f8f8f8'
        },
        infoRow: {
            display: 'grid',
            gridTemplateColumns: '76px minmax(0, 1fr)',
            gap: '12px',
            alignItems: 'start'
        },
        infoLabel: {
            color: '#666',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '150%'
        },
        infoValue: {
            color: '#333',
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: '150%',
            wordBreak: 'break-word'
        },
        button: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            height: '48px',
            whiteSpace: 'nowrap'
        }
    }

    const openRestoreHistoryPopup = (history) => {
        if (isViewMode) {
            showToast('warning', "편집중일 때만 히스토리를 복구할 수 있습니다.");
            return;
        }
        setPopUpContent(
            <div className="msg-popup restore-history" style={restoreHistoryPopupStyle.popup}>
                <div className="alert-box" style={restoreHistoryPopupStyle.alertBox}>
                    <p className="title">히스토리 복원</p>
                    <p className="single-msg" style={restoreHistoryPopupStyle.message}>
                        선택한 히스토리 버전의 내용을 현재 편집 화면에 불러옵니다.<br />
                        불러온 내용은 아직 저장되지 않으며, 저장 또는 편집 완료 시 반영됩니다.
                    </p>
                    <dl className="restore-history-info" style={restoreHistoryPopupStyle.info}>
                        <div style={restoreHistoryPopupStyle.infoRow}>
                            <dt style={restoreHistoryPopupStyle.infoLabel}>작업일시</dt>
                            <dd style={restoreHistoryPopupStyle.infoValue}>{formattedHistoryDateTime(history.updateDateTime)}</dd>
                        </div>
                        <div style={restoreHistoryPopupStyle.infoRow}>
                            <dt style={restoreHistoryPopupStyle.infoLabel}>작업상태</dt>
                            <dd style={restoreHistoryPopupStyle.infoValue}>{history.status || "-"}</dd>
                        </div>
                        <div style={restoreHistoryPopupStyle.infoRow}>
                            <dt style={restoreHistoryPopupStyle.infoLabel}>작업자</dt>
                            <dd style={restoreHistoryPopupStyle.infoValue}>{history.updateUserName || "-"}</dd>
                        </div>
                        {history.comment &&
                            <div style={restoreHistoryPopupStyle.infoRow}>
                                <dt style={restoreHistoryPopupStyle.infoLabel}>메모</dt>
                                <dd style={restoreHistoryPopupStyle.infoValue}>{history.comment}</dd>
                            </div>
                        }
                    </dl>
                    <div className="alert-btn-wrap">
                        <button className="btn-secondary" style={restoreHistoryPopupStyle.button} onClick={() => setPopUpContent(null)}>취소</button>
                        <button className="btn-primary" style={restoreHistoryPopupStyle.button} onClick={() => restoreHistory(history)}>복원</button>
                    </div>
                </div>
            </div>
        );
    }

    const restoreHistory = async (history) => {
        if (!history?.id?.startsWith("WK_")) {
            showToast('warning', "복원할 수 없는 히스토리입니다.");
            setPopUpContent(null);
            return;
        }

        const body = {
            docId: props.current.item.docId,
            historyId: history.id
        };

        const response = await fetch('api/Edit/GetHistoryDocument', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(body)
        });
        const result = await response.json();
        setPopUpContent(null);

        if (response.status === 200) {
            if (result.document) {
                const restoredData = JSON.parse(result.document);
                props.setEditorData(prev => {
                    return {
                        ...prev,
                        title: restoredData.title ?? prev.title,
                        tag: restoredData.tag ?? prev.tag,
                        relationInfo: restoredData.relationInfo ?? prev.relationInfo,
                        faq: restoredData.faq ?? prev.faq,
                        data: restoredData.data ?? prev.data
                    };
                });
                if (editor && restoredData.data) {
                    editor.render(convertDataType(restoredData.data, props.currentDocBox.docBoxPath, docId));
                }
            }
            showToast('success', "히스토리 데이터가 편집 화면에 반영되었습니다.");
        } else {
            showToast('danger', result.message || result.error || "히스토리 복구를 실패했습니다.");
        }
    }
    
    const setOpen = (name) =>{
        if(sidePanelType === name)
            setSidePanelType("");
        else
            setSidePanelType(name);
    }

    const viewSidePanel = () => {
        const panel = sidePanelType === 'info' ?
                <DocInfo openSearchDoc={openSearchDoc} removeRelatedDoc={removeRelatedDoc} isViewMode={isViewMode} 
                    docData={props.editorData} setDocData={props.setEditorData} onClose={() => setSidePanelType("")} accessType={accessType} />    
            :sidePanelType === 'history' ?
                <DocHistory docId={docId} onRestore={openRestoreHistoryPopup} onClose={() => setSidePanelType("")} />
            :sidePanelType === 'feedback' ?
                <DocFeedback docId={docId} onClose={() => setSidePanelType("")} />
            :null
        return panel;
    }
    const onQADocument = async (workingTag, requestData) => {
        const userId = LoginStates.userId;
        const body = {
            workingTag: workingTag,
            workId: props.current.item.workId,
            qAId: props.current.item.qAId || "",
            qAStatus: "1",
            devUserId: userId,
            menuPath: requestData?.path?.menuPath || "",
            location: requestData?.path?.location || "",
            fullPath: JSON.stringify(requestData?.path?.fullPath || []),
            title: requestData?.title || "",
            infomations: JSON.stringify(requestData?.type ? {
                product: requestData?.type?.type || "",
                version: requestData?.sub || "",
            } : {}),
            comment: requestData?.comment || "",
            contentJson: JSON.stringify(props.editorData || {})
        };
        const response = await fetch('api/Edit/QADocument', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(body)
        });
        const result = await response.json();
        if(response.status === 200){
            showToast('success', result.message);
            props.getManual(props.current);
        } else {
            showToast('danger', result.message);
        }
        if(workingTag === "Request") {
            setPopUpContent(null);
        }
    }
    const removeFileData = async (data) => {
        const fullData = props.editorData.data.blocks;
        const filteredData = data.filter(x => !fullData.find(d => x.filePath.includes(d.data.file?.url)));
        const response = await fetch('api/Edit/RemoveFilesByPath', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(filteredData)
        });
        const result = await response.json();
        if (response.status !== 200 || result !== "OK") {
            console.log("Error: ", response.statusText);
        }
    }
    return (
        <div className={'edit-area'}>
            <EditProperty ViewMode={isViewMode} current={props.current} setCurrent={props.setCurrent} docData={props.editorData} checkIsChange={props.checkIsChange} 
                SubmitQA={SubmitQA} onQADocument={onQADocument} onEditDocument={onEditDocument} setOpen={setOpen} menuName={props.currentDocBox.docBoxName}
                CopyFromDocument={CopyFromDocument} CopyToDocument={props.CopyToDocument} isEditable={props.isEditable} docBoxPath={props.currentDocBox.docBoxPath} /> 
                
            <div className={"editor-containder-wrap"}>
                <div className={"editor-container"}>                
                    <div className={"editor-section"}>
                        <div className={"title-section"}>
                            <textarea name="title" placeholder={props.editorData?.title ?? "제목"} 
                                value={props.editorData?.title ?? ""} onChange={handleChange} readOnly={isViewMode} />
                        </div>
                        <Editor ViewMode={isViewMode} folderName={props.currentDocBox.docBoxPath} setEditorData={props.setEditorData} editorData={props.editorData} 
                            docId={docId} setValid={setValid} setEditor={setEditor} setRemoveData={setRemoveData} />
                    </div>
                </div>    
                {viewSidePanel()}
            </div>
            <PopUp content={popUpContent} setContent={setPopUpContent} />
        </div>
    );
}
export default EditArea;

const MetadataPreviewPopup = ({ metadata, currentTags, onClose, onApprove }) => {
    const [isApproveHover, setIsApproveHover] = useState(false);
    const [isCancelHover, setIsCancelHover] = useState(false);
    const [keywords, setKeywords] = useState(() => getMetadataKeywords(metadata));
    const tags = Array.isArray(currentTags) ? currentTags : [];
    const tagKeys = new Set(tags.map(tag => `${tag}`.trim().toLowerCase()).filter(tag => tag !== ""));
    const newTags = keywords.filter(keyword => !tagKeys.has(keyword.toLowerCase()));
    const duplicatedTags = keywords.filter(keyword => tagKeys.has(keyword.toLowerCase()));

    const styles = {
        modal: {
            width: "560px",
            padding: "32px",
            borderRadius: "16px"
        },
        content: {
            display: "flex",
            flexDirection: "column",
            gap: "14px"
        },
        section: {
            padding: "16px",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            backgroundColor: "#FFF"
        },
        sectionTitle: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "12px"
        },
        count: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "22px",
            height: "22px",
            padding: "0 7px",
            borderRadius: "999px",
            backgroundColor: "#F3F4F6",
            color: "#666",
            fontSize: "12px",
            fontWeight: 700
        },
        tagList: {
            display: "flex",
            flexWrap: "wrap",
            gap: "8px"
        },
        tag: {
            display: "inline-flex",
            alignItems: "center",
            maxWidth: "100%",
            minHeight: "30px",
            padding: "6px 12px",
            borderRadius: "999px",
            fontSize: "13px",
            fontWeight: 600,
            lineHeight: "18px",
            wordBreak: "break-word",
            gap: "6px"
        },
        removeTagButton: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "18px",
            height: "18px",
            padding: 0,
            border: 0,
            borderRadius: "50%",
            background: "transparent",
            color: "inherit",
            fontSize: "16px",
            lineHeight: 1,
            cursor: "pointer"
        },
        empty: {
            margin: 0,
            color: "#999",
            fontSize: "14px"
        },
        buttonWrap: {
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            marginTop: "22px",
            paddingTop: "18px",
            borderTop: "1px solid #EEE"
        },
        button: {
            minWidth: "96px",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "150%",
            margin: 0,
            textOverflow: "ellipsis",
            overflow: "hidden",
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            cursor: "pointer"
        },
        cancelButton: {
            backgroundColor: "transparent",
            color: isCancelHover ? "#4668D9" : "#284cc4",
            border: `1px solid ${isCancelHover ? "#4668D9" : "#284cc4"}`
        },
        approveButton: {
            padding: "12px 20px",
            borderRadius: "8px",
            background: isApproveHover ? "#3158D6" : "#284cc4",
            cursor: "pointer",
            color: "#fff",
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "150%",
            margin: 0,
            textOverflow: "ellipsis",
            overflow: "hidden",
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            minWidth: "96px"
        }
    };

    const getTagStyle = (type) => {
        if (type === "new") {
            return {
                backgroundColor: "rgba(40, 196, 178, 0.12)",
                border: "1px solid rgba(40, 196, 178, 0.45)",
                color: "#168F82"
            };
        }

        if (type === "duplicate") {
            return {
                backgroundColor: "#F5F5F5",
                border: "1px solid #DDD",
                color: "#777"
            };
        }

        return {
            backgroundColor: "#F7F9FF",
            border: "1px solid #DDE5FF",
            color: "#284CC4"
        };
    }

    const removeKeyword = (keyword) => {
        setKeywords(prev => prev.filter(item => item !== keyword));
    }

    const renderTags = (items, emptyText, type, removable = false) => {
        if (!items || items.length === 0) {
            return <p style={styles.empty}>{emptyText}</p>;
        }

        return (
            <div className="metadata-tag-list" style={styles.tagList}>
                {items.map((tag, index) => (
                    <span
                        className="metadata-tag"
                        key={`${tag}-${index}`}
                        style={{ ...styles.tag, ...getTagStyle(type) }}
                    >
                        {tag}
                        {removable &&
                            <button
                                type="button"
                                aria-label={`${tag} 태그 삭제`}
                                title="태그 삭제"
                                style={styles.removeTagButton}
                                onClick={() => removeKeyword(tag)}
                            >
                                ×
                            </button>
                        }
                    </span>
                ))}
            </div>
        );
    }

    const renderTagSection = (title, items, emptyText, type, removable = false) => (
        <div className="memo" style={styles.section}>
            <div className="cate-top" style={styles.sectionTitle}>
                <h4 className="cate-title">{title}</h4>
                <span style={styles.count}>{items.length}</span>
            </div>
            {renderTags(items, emptyText, type, removable)}
        </div>
    )

    return (
        <div className="msg-popup center-box">
            <div className="modal" style={styles.modal}>
                <div className="modal-top">
                    <h3 className="modal-title">태그 추출 확인</h3>
                    <button className="btn-close" onClick={onClose}>
                        <i className="icoKdc32 close"></i>
                    </button>
                </div>
                <div className="modal-cnts">
                    <div className="metadata-preview" style={styles.content}>
                        {renderTagSection("추출된 태그", keywords, "추출된 태그가 없습니다.", "default", true)}
                        {renderTagSection("추가될 태그", newTags, "새로 추가할 태그가 없습니다.", "new")}
                        {duplicatedTags.length > 0 &&
                            renderTagSection("이미 등록된 태그", duplicatedTags, "", "duplicate")
                        }
                    </div>
                </div>
                <div className="alert-btn-wrap" style={styles.buttonWrap}>
                    <button
                        className="btn-secondary"
                        style={{ ...styles.button, ...styles.cancelButton }}
                        onMouseEnter={() => setIsCancelHover(true)}
                        onMouseLeave={() => setIsCancelHover(false)}
                        onClick={onClose}
                    >
                        취소
                    </button>
                    <button
                        className="btn-primary"
                        style={styles.approveButton}
                        onMouseEnter={() => setIsApproveHover(true)}
                        onMouseLeave={() => setIsApproveHover(false)}
                        onClick={() => onApprove(keywords)}
                    >
                        완료
                    </button>
                </div>
            </div>
        </div>
    );
}
